import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbAdjustments } from "react-icons/tb";
import { formatCurrency } from "../../../../../Utils/formatters";
import { BaasLimitUsageCard } from "../../components/BaasLimitUsageCard";
import { getBaasStablesLimitItemForOperation } from "../baasStablesLimitsConstants";

/** API `used_cents` / `total_cents` — same scale as IB Contas (centavos). Never major units. */
const formatLimit = (cents) => formatCurrency(Number(cents) || 0);

const LimitsHeader = ({ configureTo, compact }) => (
  <div className={`flex items-start justify-between gap-3 ${compact ? "" : "mb-4"}`}>
    <div className="min-w-0">
      <p className="dashboard-section-label mb-1">
        {intl.get("baas.stablesWallet.limits.section")}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {intl.get("baas.stablesWallet.limits.subtitle")}
      </p>
    </div>
    {configureTo ? (
      <Link
        to={configureTo}
        className="link-no-zoom inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[#e7a73f] transition hover:text-brand-dark dark:hover:text-gray-100"
      >
        <TbAdjustments className="h-3.5 w-3.5" aria-hidden />
        {intl.get("baas.stablesWallet.limits.viewLimits")}
      </Link>
    ) : null}
  </div>
);

const resolveLimitRows = (limits) => {
  const apiItems = Array.isArray(limits?.items)
    ? limits.items.filter((item) => Number(item?.max ?? 0) > 0 || Number(item?.used ?? 0) > 0)
    : [];

  if (apiItems.length > 0) {
    return apiItems.slice(0, 4).map((item) => ({
      key: item.id || item.label,
      label: item.label || item.id,
      description: item.description || "",
      used: Number(item.used ?? 0),
      max: Number(item.max ?? 0),
      usage: Array.isArray(item.usage) ? item.usage : [],
    }));
  }

  return [
    {
      key: "daily",
      label: intl.get("baas.stablesWallet.limits.daily"),
      description: "",
      used: limits?.daily?.used ?? 0,
      max: limits?.daily?.max ?? 0,
      usage: [],
    },
    {
      key: "monthly",
      label: intl.get("baas.stablesWallet.limits.monthly"),
      description: "",
      used: limits?.monthly?.used ?? 0,
      max: limits?.monthly?.max ?? 0,
      usage: [],
    },
  ];
};

/**
 * @param {{ limits: object|null, operation?: string, configureTo?: string }} props
 * When `operation` is set (buy/sell/swap/withdraw), shows that op’s limit (IB-style) even if 0.
 * `configureTo` is a “Ver limites” deep-link into Contas → account detail → Limites (not Configurar).
 */
export const BaasStablesLimitsBar = ({ limits, operation = null, configureTo = null }) => {
  if (!limits && !operation) return null;

  if (operation) {
    const item = getBaasStablesLimitItemForOperation(limits, operation);
    if (!item) return null;

    return (
      <section className="app-card overflow-hidden p-4" data-section="limits">
        <LimitsHeader configureTo={configureTo} compact />
        <div className="mt-4">
          <BaasLimitUsageCard
            label={item.label || operation}
            description={item.description}
            used={item.used}
            max={item.max}
            usage={item.usage}
            formatValue={formatLimit}
          />
        </div>
      </section>
    );
  }

  const rows = resolveLimitRows(limits);
  const hasAnyLimit = rows.some((row) => row.max > 0 || row.used > 0);

  if (!hasAnyLimit) {
    return (
      <section className="app-card overflow-hidden p-4" data-section="limits">
        <LimitsHeader configureTo={configureTo} />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.stablesWallet.limits.empty")}
        </p>
      </section>
    );
  }

  return (
    <section className="app-card overflow-hidden p-4" data-section="limits">
      <LimitsHeader configureTo={configureTo} />
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((row) => (
          <BaasLimitUsageCard
            key={row.key}
            label={row.label}
            description={row.description}
            used={row.used}
            max={row.max}
            usage={row.usage}
            formatValue={formatLimit}
          />
        ))}
      </div>
    </section>
  );
};
